import React, { useEffect, useState, useRef } from "react";
import { flushSync } from "react-dom";
import './horizonTable.css';
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import html2canvas from "html2canvas";
import onsiteproLogo from '../../assets/logo2 2.png';
import HandArmVibrationEditModalContent from './HandArmVibrationEditModalContent.js';
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
function HandArmVibrationPage() {
  const canEditDelete = canEditDeleteRecords();
  const [appUsers, setAppUsers] = useState([]);
  const [handArmVibrations, setHandArmVibrations] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 4;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editHandArmVibration, setEditHandArmVibration] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [newHandArmVibration, setNewHandArmVibration] = useState({
    formUuid: '',
    assetIdNo: '',
    vibrationLevel: '',
    maximumDailyUse: '',
    pointsPerMinForUse: '',
    userConfirmation: 'No'
  });
  const [uploadingKey, setUploadingKey] = useState(null);
  const generatingPdfRef = useRef(false);
  const [havPdfRender, setHavPdfRender] = useState(null);
  const havPdfRef = useRef(null);

  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);


  useEffect(() => {
    async function fetchHandArmVibrations() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/hand-arm-vibration", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setHandArmVibrations(data.data);
        } else {
          setHandArmVibrations([]);
        }
      } catch {
        setHandArmVibrations([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchHandArmVibrations();
    fetchAppUsers();
  }, []);


  // Improved search: split search string into words, ignore extra spaces, match if any word is present in any field
  const filtered = handArmVibrations.filter(r => {
    const toolStr = Array.isArray(r.toolLabelsData)
      ? r.toolLabelsData.map(t => [t.assetLabel, t.assetId, t.vibrationLevel].filter(Boolean).join(' ')).join(' ')
      : '';
    const logStr = Array.isArray(r.logData) ? JSON.stringify(r.logData) : '';
    const fields = [
      r.operatorName,
      r.assetIdNo,
      r.vibrationLevel,
      r.maximumDailyUse,
      r.pointsPerMinForUse,
      toolStr,
      logStr
    ].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // Add Hand Arm Vibration
  const handleAdd = async () => {
    // Basic validation
    if (!newHandArmVibration.assetIdNo || !newHandArmVibration.vibrationLevel || !newHandArmVibration.maximumDailyUse || !newHandArmVibration.pointsPerMinForUse) {
      toast.error("All fields are required");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get user details for saving firstName and lastName if userId is available
      let addedByFirstName = '';
      let addedByLastName = '';
      if (newHandArmVibration.userId) {
        const user = appUsers.find(u => u._id === newHandArmVibration.userId);
        if (user) {
          addedByFirstName = user.firstName || '';
          addedByLastName = user.lastName || '';
        }
      }
      
      const vibrationToSave = {
        ...newHandArmVibration,
        formUuid: ensureFormUuid(newHandArmVibration.formUuid),
        addedByFirstName,
        addedByLastName
      };
      
      const res = await fetch(baseUrl + "/api/hand-arm-vibration", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(vibrationToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        setHandArmVibrations([...handArmVibrations, data.data]);
        setShowAddModal(false);
        setNewHandArmVibration({ formUuid: '', assetIdNo: '', vibrationLevel: '', maximumDailyUse: '', pointsPerMinForUse: '', userConfirmation: 'No' });
        toast.success("Hand arm vibration log added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Helper function to parse array string to array
  const parseArrayString = (str) => {
    if (!str) return ['', '', ''];
    if (typeof str !== 'string') return ['', '', ''];
    // Remove brackets and split by comma
    const cleaned = str.replace(/^\[|\]$/g, '').trim();
    if (!cleaned) return ['', '', ''];
    const items = cleaned.split(',').map(item => item.trim());
    // Ensure we have at least 3 items
    while (items.length < 3) items.push('');
    return items.slice(0, 3);
  };

  // Helper function to format array to array string
  const formatArrayString = (arr) => {
    if (!Array.isArray(arr)) return '[]';
    const cleaned = arr.map(item => String(item || '').trim()).filter(item => item !== '');
    return `[${cleaned.join(', ')}]`;
  };

  const HAV_LOG_ROWS_PER_BLOCK = 4;
  const HAV_DEFAULT_TOOL_LABELS = ['MTI 512', 'MTI 510', 'MTI 511'];
  const HAV_MIN_ROWS_FOR_EDIT = 1;

  const emptyHavLogRow = () => ({
    date: '',
    task: '',
    makeModel: '',
    triggerTime: '',
    pointsPerMin: '',
    totalPoints: ''
  });

  const normalizeHavRows = (rows) => {
    const r = Array.isArray(rows) ? rows.map((row) => ({
      date: row?.date != null ? String(row.date) : '',
      task: row?.task != null ? String(row.task) : '',
      makeModel: row?.makeModel != null ? String(row.makeModel) : '',
      triggerTime: row?.triggerTime != null ? String(row.triggerTime) : '',
      pointsPerMin: row?.pointsPerMin != null ? String(row.pointsPerMin) : '',
      totalPoints: row?.totalPoints != null ? String(row.totalPoints) : ''
    })) : [];
    return r;
  };

  const ensureMinRows = (rows, minRows = 1) => {
    const r = normalizeHavRows(rows);
    while (r.length < minRows) r.push(emptyHavLogRow());
    return r;
  };

  const normalizeLogDataForSave = (ld) => {
    if (!Array.isArray(ld)) return [];
    const isEmptyRow = (r) => {
      const vals = [
        r?.date,
        r?.task,
        r?.makeModel,
        r?.triggerTime,
        r?.pointsPerMin,
        r?.totalPoints
      ].map((v) => String(v ?? '').trim());
      return vals.every((v) => v === '');
    };

    return ld.map((w) => {
      const rows = normalizeHavRows(w?.rows);
      const filtered = rows.filter((r) => !isEmptyRow(r));
      return {
        weekDate: w?.weekDate != null ? String(w.weekDate) : '',
        rows: filtered
      };
    });
  };

  const normalizeLogDataForEdit = (ld) => {
    if (!Array.isArray(ld) || ld.length === 0) {
      return [{ weekDate: '', rows: ensureMinRows([], HAV_MIN_ROWS_FOR_EDIT) }];
    }
    return ld.map((w) => ({
      weekDate: w?.weekDate != null ? String(w.weekDate) : '',
      rows: ensureMinRows(w?.rows, HAV_MIN_ROWS_FOR_EDIT)
    }));
  };

  const normalizeToolLabelsForEdit = (tl) => {
    const src = Array.isArray(tl) ? tl : [];
    return HAV_DEFAULT_TOOL_LABELS.map((label, i) => {
      const t = src[i] || {};
      let isUsed = t.isUsed;
      if (isUsed === 'Yes') isUsed = true;
      if (isUsed === 'No') isUsed = false;
      return {
        assetLabel: t.assetLabel != null && String(t.assetLabel).trim() !== '' ? String(t.assetLabel) : label,
        assetId: t.assetId != null ? String(t.assetId) : '',
        vibrationLevel: t.vibrationLevel != null ? String(t.vibrationLevel) : '',
        maxDailyUse: t.maxDailyUse != null ? String(t.maxDailyUse) : '',
        pointsPerMin: t.pointsPerMin != null ? String(t.pointsPerMin) : '',
        isUsed: isUsed === true || isUsed === false ? isUsed : null
      };
    });
  };

  const sumBlockTotalPoints = (rows) => {
    if (!Array.isArray(rows)) return '';
    const n = rows.reduce((acc, row) => acc + (parseFloat(String(row?.totalPoints || '').replace(/,/g, '')) || 0), 0);
    if (!n) return '';
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  };

  const handleEdit = (hav) => {
    const toolLabelsData = normalizeToolLabelsForEdit(hav.toolLabelsData);
    const userConfirmationArray = toolLabelsData.map((t) =>
      t.isUsed === true ? 'Yes' : 'No'
    );
    const parsedHav = {
      ...hav,
      formUuid: ensureFormUuid(hav?.formUuid),
      logData: normalizeLogDataForEdit(hav.logData),
      toolLabelsData,
      operatorName: hav.operatorName != null ? String(hav.operatorName) : '',
      signature: hav.signature != null ? String(hav.signature) : '',
      assetIdNoArray: toolLabelsData.map((t) => t.assetId),
      vibrationLevelArray: toolLabelsData.map((t) => t.vibrationLevel),
      maximumDailyUseArray: toolLabelsData.map((t) => t.maxDailyUse),
      pointsPerMinForUseArray: toolLabelsData.map((t) => t.pointsPerMin),
      userConfirmationArray
    };
    setEditHandArmVibration(parsedHav);
    setShowEditModal(true);
  };

  const updateHavLogField = (weekIdx, rowIdx, field, value) => {
    setEditHandArmVibration((prev) => {
      const logData = (prev.logData || []).map((w) => ({
        weekDate: w.weekDate,
        rows: (w.rows || []).map((r) => ({ ...r }))
      }));
      if (!logData[weekIdx]) return prev;
      while (logData[weekIdx].rows.length <= rowIdx) logData[weekIdx].rows.push(emptyHavLogRow());
      const row = { ...logData[weekIdx].rows[rowIdx], [field]: value };
      // Total Points = Trigger Time (mins) × Points per minute (auto-calculated).
      if (field === 'triggerTime' || field === 'pointsPerMin') {
        const t = parseFloat(String(row.triggerTime || '').replace(/[^0-9.]/g, ''));
        const p = parseFloat(String(row.pointsPerMin || '').replace(/[^0-9.]/g, ''));
        if (!Number.isNaN(t) && !Number.isNaN(p)) {
          const tp = t * p;
          row.totalPoints = Number.isInteger(tp) ? String(tp) : String(Math.round(tp * 100) / 100);
        } else {
          row.totalPoints = '';
        }
      }
      logData[weekIdx].rows[rowIdx] = row;
      return { ...prev, logData };
    });
  };

  const updateWeekDate = (weekIdx, value) => {
    setEditHandArmVibration((prev) => {
      const logData = (prev.logData || []).map((w, i) =>
        i === weekIdx ? { ...w, weekDate: value } : { ...w, rows: (w.rows || []).map((r) => ({ ...r })) }
      );
      return { ...prev, logData };
    });
  };

  const addHavLogWeek = () => {
    setEditHandArmVibration((prev) => ({
      ...prev,
      logData: [...(prev.logData || []), { weekDate: '', rows: ensureMinRows([], HAV_MIN_ROWS_FOR_EDIT) }]
    }));
  };

  const removeHavLogWeek = (weekIdx) => {
    setEditHandArmVibration((prev) => {
      const logData = [...(prev.logData || [])];
      if (logData.length <= 1) return prev;
      logData.splice(weekIdx, 1);
      return { ...prev, logData };
    });
  };

  const updateToolLabelField = (idx, field, value) => {
    setEditHandArmVibration((prev) => {
      const toolLabelsData = (prev.toolLabelsData || []).map((t) => ({ ...t }));
      if (!toolLabelsData[idx]) return prev;
      toolLabelsData[idx] = { ...toolLabelsData[idx], [field]: value };
      const assetIdNoArray = [...(prev.assetIdNoArray || ['', '', ''])];
      const vibrationLevelArray = [...(prev.vibrationLevelArray || ['', '', ''])];
      const maximumDailyUseArray = [...(prev.maximumDailyUseArray || ['', '', ''])];
      const pointsPerMinForUseArray = [...(prev.pointsPerMinForUseArray || ['', '', ''])];
      if (field === 'assetId') assetIdNoArray[idx] = value;
      if (field === 'vibrationLevel') vibrationLevelArray[idx] = value;
      if (field === 'maxDailyUse') maximumDailyUseArray[idx] = value;
      if (field === 'pointsPerMin') pointsPerMinForUseArray[idx] = value;
      return {
        ...prev,
        toolLabelsData,
        assetIdNoArray,
        vibrationLevelArray,
        maximumDailyUseArray,
        pointsPerMinForUseArray
      };
    });
  };

  const setToolLabelUsed = (idx, isYes) => {
    setEditHandArmVibration((prev) => {
      const toolLabelsData = (prev.toolLabelsData || []).map((t) => ({ ...t }));
      if (!toolLabelsData[idx]) return prev;
      toolLabelsData[idx] = { ...toolLabelsData[idx], isUsed: isYes ? true : false };
      const userConfirmationArray = [...(prev.userConfirmationArray || ['No', 'No', 'No'])];
      userConfirmationArray[idx] = isYes ? 'Yes' : 'No';
      return { ...prev, toolLabelsData, userConfirmationArray };
    });
  };

  const handleEditSave = async () => {
    const assetIdNoArray = editHandArmVibration.assetIdNoArray || ['', '', ''];
    const vibrationLevelArray = editHandArmVibration.vibrationLevelArray || ['', '', ''];
    const maximumDailyUseArray = editHandArmVibration.maximumDailyUseArray || ['', '', ''];
    const pointsPerMinForUseArray = editHandArmVibration.pointsPerMinForUseArray || ['', '', ''];
    const userConfirmationArray = editHandArmVibration.userConfirmationArray || ['No', 'No', 'No'];
    
    const assetIdNo = formatArrayString(assetIdNoArray);
    const vibrationLevel = formatArrayString(vibrationLevelArray);
    const maximumDailyUse = formatArrayString(maximumDailyUseArray);
    const pointsPerMinForUse = formatArrayString(pointsPerMinForUseArray);
    const userConfirmation = formatArrayString(userConfirmationArray);
    
    const hasNewPayload =
      (Array.isArray(editHandArmVibration.logData) && editHandArmVibration.logData.length > 0) ||
      (Array.isArray(editHandArmVibration.toolLabelsData) && editHandArmVibration.toolLabelsData.length > 0) ||
      (editHandArmVibration.operatorName && String(editHandArmVibration.operatorName).trim() !== '');
    const legacyComplete =
      assetIdNo && assetIdNo !== '[]' &&
      vibrationLevel && vibrationLevel !== '[]' &&
      maximumDailyUse && maximumDailyUse !== '[]' &&
      pointsPerMinForUse && pointsPerMinForUse !== '[]' &&
      userConfirmation && userConfirmation !== '[]';
    if (!hasNewPayload && !legacyComplete) {
      toast.error("Enter tool card details or sync data from the app");
      return;
    }
    
    if (!editHandArmVibration || !editHandArmVibration._id) {
      toast.error("Invalid record to update");
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      const vibrationToSave = {};
      if (legacyComplete) {
        vibrationToSave.assetIdNo = assetIdNo;
        vibrationToSave.vibrationLevel = vibrationLevel;
        vibrationToSave.maximumDailyUse = maximumDailyUse;
        vibrationToSave.pointsPerMinForUse = pointsPerMinForUse;
        vibrationToSave.userConfirmation = userConfirmation;
      }
      if (editHandArmVibration.userId) {
        vibrationToSave.userId = editHandArmVibration.userId;
      }
      if (editHandArmVibration.operatorName !== undefined && editHandArmVibration.operatorName !== null) {
        vibrationToSave.operatorName = editHandArmVibration.operatorName;
      }
      if (Array.isArray(editHandArmVibration.logData)) {
        vibrationToSave.logData = normalizeLogDataForSave(editHandArmVibration.logData);
      }
      if (Array.isArray(editHandArmVibration.toolLabelsData)) {
        vibrationToSave.toolLabelsData = editHandArmVibration.toolLabelsData;
      }
      if (editHandArmVibration.signature !== undefined) {
        vibrationToSave.signature = editHandArmVibration.signature;
      }
      if (editHandArmVibration.whatIsIt !== undefined) vibrationToSave.whatIsIt = editHandArmVibration.whatIsIt;
      if (editHandArmVibration.whatIsHavs !== undefined) vibrationToSave.whatIsHavs = editHandArmVibration.whatIsHavs;
      if (editHandArmVibration.whatIsCarpalTunnel !== undefined) vibrationToSave.whatIsCarpalTunnel = editHandArmVibration.whatIsCarpalTunnel;
      if (editHandArmVibration.whoIsAtRisk !== undefined) vibrationToSave.whoIsAtRisk = editHandArmVibration.whoIsAtRisk;
      if (editHandArmVibration.whatAreTheSymptoms !== undefined) vibrationToSave.whatAreTheSymptoms = editHandArmVibration.whatAreTheSymptoms;
      if (editHandArmVibration.doINeedHealthSurveillance !== undefined) vibrationToSave.doINeedHealthSurveillance = editHandArmVibration.doINeedHealthSurveillance;
      if (editHandArmVibration.controlMeasuresList !== undefined) vibrationToSave.controlMeasuresList = editHandArmVibration.controlMeasuresList;
      if (editHandArmVibration.controlMeasuresNote !== undefined) vibrationToSave.controlMeasuresNote = editHandArmVibration.controlMeasuresNote;
      vibrationToSave.formUuid = ensureFormUuid(editHandArmVibration.formUuid);
      
      const res = await fetch(baseUrl + `/api/hand-arm-vibration/${editHandArmVibration._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(vibrationToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Update the record in the list with the response data from API
        setHandArmVibrations(handArmVibrations.map(r => 
          r._id === editHandArmVibration._id ? data.data : r
        ));
        setShowEditModal(false);
        setEditHandArmVibration(null);
        toast.success("Hand arm vibration log updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch (error) {
      console.error("Error updating hand arm vibration:", error);
      toast.error("Failed to update");
    }
  };

  // Delete Hand Arm Vibration
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/hand-arm-vibration/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setHandArmVibrations(handArmVibrations.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const operatorForUpload = (
      sharePointUploadRecord?.operatorName ||
      sharePointUploadRecord?.toolLabelsData?.[0]?.assetId ||
      sharePointUploadRecord?.assetIdNo ||
      ''
    ).trim();

    if (!operatorForUpload) {
      toast.error('Operator is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: operatorForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const generatePDF = async (record, options = {}) => {
    if (generatingPdfRef.current) {
      return;
    }
    generatingPdfRef.current = true;

    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;

    try {
      const snapshot = {
        ...record,
        formUuid: ensureFormUuid(record?.formUuid),
        logData: normalizeLogDataForEdit(record.logData),
        toolLabelsData: normalizeToolLabelsForEdit(record.toolLabelsData),
        operatorName: record.operatorName != null ? String(record.operatorName) : '',
        signature: record.signature != null ? String(record.signature) : ''
      };

      const captureHtml = async () => {
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
          try {
            await document.fonts.ready;
          } catch (_) {}
        }
        await new Promise((r) => setTimeout(r, 200));
        const node = havPdfRef.current;
        if (!node) {
          throw new Error("PDF render unavailable");
        }
        return html2canvas(node, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff"
        });
      };

      flushSync(() => {
        setHavPdfRender({ record: snapshot, part: "aboveToolLabels" });
      });
      const canvas1 = await captureHtml();

      flushSync(() => {
        setHavPdfRender({ record: snapshot, part: "toolLabelsOnly" });
      });
      const canvas2 = await captureHtml();

      flushSync(() => setHavPdfRender(null));

      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const pageInnerH = pageHeight - margin * 2;
      const imgWidth = pageWidth - margin * 2;

      const drawClassicPdfHeader = () =>
        new Promise((resolve) => {
          const primaryColor = [139, 22, 52];
          doc.setFillColor(...primaryColor);
          doc.rect(0, 0, pageWidth, 25, "F");
          const logoImg = new Image();
          logoImg.crossOrigin = "anonymous";
          logoImg.onload = () => {
            try {
              doc.addImage(logoImg, "PNG", 5, 5, 60, 15);
            } catch (_) {
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.setTextColor(0, 0, 0);
              doc.text("LOGO", 35, 12);
            }
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.setTextColor(255, 255, 255);
            doc.text("HAND ARM VIBRATION LOG", 75, 12);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text("Official Hand Arm Vibration Documentation", 75, 18);
            resolve();
          };
          logoImg.onerror = () => {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(255, 255, 255);
            doc.text("LOGO", 35, 12);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.text("HAND ARM VIBRATION LOG", 75, 12);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.text("Official Hand Arm Vibration Documentation", 75, 18);
            resolve();
          };
          logoImg.src = onsiteproLogo;
        });

      await drawClassicPdfHeader();

      const HEADER_BAR_MM = 25;
      const HEADER_BODY_GAP_MM = 3;
      const firstBodyTopMm = HEADER_BAR_MM + HEADER_BODY_GAP_MM;

      const addCanvasToDoc = (canvas, opts = {}) => {
        const topInsetFirstPage = opts.topInsetFirstPage ?? margin;
        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const slotFirstPage = pageHeight - topInsetFirstPage - margin;
        let heightLeft = imgHeight;
        let y = topInsetFirstPage;
        doc.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
        heightLeft -= slotFirstPage;
        while (heightLeft > 0.5) {
          y = margin - (imgHeight - heightLeft);
          doc.addPage();
          doc.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
          heightLeft -= pageInnerH;
        }
      };

      addCanvasToDoc(canvas1, { topInsetFirstPage: firstBodyTopMm });
      doc.addPage();
      addCanvasToDoc(canvas2);

      const safeAssetId = String(
        record.assetIdNo ||
        (record.toolLabelsData && record.toolLabelsData[0] && record.toolLabelsData[0].assetId) ||
        record.operatorName ||
        "HandArmVibration"
      ).replace(/[\\/:"*?<>|]+/g, "-");

      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        // No site is captured on this form, so the name is operator + date only.
        fileName = buildSharePointFileName([
          record.operatorName || uploadConfig.siteName,
          toDdMmYyyy(record.date),
        ]);
      } else {
        fileName = `${safeAssetId}_HandArmVibration.pdf`;
      }

      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const key = `${record._id || ""}|${record.assetIdNo || ""}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
          const dataUri = doc.output("datauristring");

          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "Hand Arm Vibration Log"
          };

          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = "SharePoint upload failed";
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("PDF generated successfully!");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to generate PDF");
    } finally {
      try {
        flushSync(() => setHavPdfRender(null));
      } catch (_) {
        setHavPdfRender(null);
      }
      generatingPdfRef.current = false;
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Hand Arm Vibration Log</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr 0.9fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Operator</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Tool / asset</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Log weeks</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                const toolSummary = Array.isArray(r.toolLabelsData) && r.toolLabelsData.length
                  ? r.toolLabelsData.map(t => t.assetLabel || t.assetId || '—').filter(Boolean).join(', ')
                  : (r.assetIdNo || '—');
                const logWeeks = Array.isArray(r.logData) ? r.logData.length : 0;
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr 1fr 0.9fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.operatorName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px', wordBreak: 'break-word'}}>{toolSummary}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{logWeeks || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.assetIdNo || (r.toolLabelsData && r.toolLabelsData[0] && r.toolLabelsData[0].assetId) || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleDelete(r._id)}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editHandArmVibration && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Hand Arm Vibration Log</h5>
                <button type="button" className="btn-close" onClick={()=>setShowEditModal(false)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '85vh', overflow: 'auto', background: '#d9d9d9', padding: 12 }}>
                <HandArmVibrationEditModalContent
                  edit={editHandArmVibration}
                  setEditHandArmVibration={setEditHandArmVibration}
                  sumBlockTotalPoints={sumBlockTotalPoints}
                  updateHavLogField={updateHavLogField}
                  updateWeekDate={updateWeekDate}
                  addHavLogWeek={addHavLogWeek}
                  removeHavLogWeek={removeHavLogWeek}
                  updateToolLabelField={updateToolLabelField}
                  setToolLabelUsed={setToolLabelUsed}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Hand Arm Vibration Log File"
        importPath="hand-arm-vibration/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setHandArmVibrations((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete Hand Arm Vibration Log</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {havPdfRender && (
        <div
          ref={havPdfRef}
          style={{ position: "fixed", left: -12000, top: 0, width: 1080, background: "#fff" }}
          aria-hidden
        >
          <HandArmVibrationEditModalContent
            edit={havPdfRender.record}
            readOnly
            pdfCapturePart={havPdfRender.part}
            sumBlockTotalPoints={sumBlockTotalPoints}
            updateHavLogField={() => {}}
            updateWeekDate={() => {}}
            addHavLogWeek={() => {}}
            removeHavLogWeek={() => {}}
            updateToolLabelField={() => {}}
            setToolLabelUsed={() => {}}
            setEditHandArmVibration={() => {}}
          />
        </div>
      )}

    </>
  );
}

export default HandArmVibrationPage;
